import { useQuery } from "../data/useQuery.js";
import { useBoundQuery, FillPrompt } from "../data/DwSql.js";
import { ErrorBadge } from "./ErrorBadge.js";
import { fmtNumber, fmtPercent } from "./format.js";
import type { QueryResult } from "../../shared/types.js";
import type { VizProps } from "./registry.js";

// Funnel: one row per stage (stage, count) in order. Each stage is a bar whose
// width tracks its count, with the count, % of top, and step-through % beside
// it. `log=true` log-scales the width so steep drop-offs stay legible.
export function FunnelViz({ source, attrs }: VizProps) {
  const { sql, values, path, status, missing } = useBoundQuery(source.trim());
  const q = useQuery(status === "ready" ? sql : null, {
    // Explicit attr overrides the query's @maxAge pragma; absent → pragma →
    // forever (useQuery sends maxAgeDefault).
    ttl: attrs.maxAge ?? attrs.ttl,
    values,
    path,
  });
  return (
    <div className="dw-embed dw-embed-chart">
      {status === "blocked" && <FillPrompt names={missing} />}
      {status === "ready" && q.status === "loading" && (
        <div className="dw-loading">Running…</div>
      )}
      {status === "ready" && q.status === "error" && (
        <ErrorBadge message={q.error.message} />
      )}
      {status === "ready" && q.status === "ready" && (
        <Funnel result={q.data} attrs={attrs} />
      )}
    </div>
  );
}

function Funnel({
  result,
  attrs,
}: {
  result: QueryResult;
  attrs: Record<string, string>;
}) {
  const colNames = result.columns.map((c) => c.name);
  const stageKey =
    (attrs.stage && colNames.includes(attrs.stage) && attrs.stage) ||
    (attrs.x && colNames.includes(attrs.x) && attrs.x) ||
    colNames[0];
  const si = colNames.indexOf(stageKey);
  const valueCol =
    (attrs.value && colNames.includes(attrs.value) && attrs.value) ||
    colNames.find((c) => c !== stageKey) ||
    colNames[0];
  const vi = colNames.indexOf(valueCol);

  const rows = result.rows.map((r) => ({
    stage: String(r[si] ?? ""),
    count: Number(r[vi] ?? 0) || 0,
  }));
  if (rows.length === 0) return <div className="dw-empty">No rows.</div>;

  const top = rows[0].count || 1;
  const useLog = !!attrs.log && attrs.log !== "false";
  const logMax = Math.log10(Math.max(top, 10) + 1);
  const widthPct = (c: number) =>
    useLog
      ? Math.max((Math.log10(Math.max(c, 0) + 1) / logMax) * 100, 2)
      : Math.max((c / top) * 100, 1);

  return (
    <div className="dw-funnel">
      {rows.map((r, i) => {
        const fromTop = top > 0 ? (r.count / top) * 100 : 0;
        const fromPrev =
          i === 0 || rows[i - 1].count === 0
            ? null
            : (r.count / rows[i - 1].count) * 100;
        return (
          <div className="dw-funnel-row" key={i}>
            <div className="dw-funnel-head">
              <span className="dw-funnel-stage">{r.stage}</span>
              <span className="dw-funnel-nums">
                <strong>{fmtNumber(r.count)}</strong>
                <span className="dw-muted"> · {fmtPercent(fromTop)} of top</span>
                {fromPrev != null && (
                  <span className="dw-muted"> · {fmtPercent(fromPrev)} step</span>
                )}
              </span>
            </div>
            <div className="dw-funnel-track">
              <div
                className="dw-funnel-bar"
                style={{ width: `${widthPct(r.count)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
